import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Save,
  Send,
  Users,
  Eye,
  AlertCircle,
  Info,
  Mail,
  Target,
  Plus,
  X,
  Upload,
  UserPlus,
  CheckCircle
} from 'lucide-react';
import { AdminApis } from '../../apis/adminApi';

interface CampaignFormData {
  title: string;
  subject: string;
  message: string;
  recipient_type: 'criteria' | 'custom' | 'both';
  criteria: {
    user_status: string;
    subscription_status: string;
    user_role: string;
    inactive_days: string;
    registration_period: string;
    include_admins: boolean;
  };
  custom_emails: Array<{
    email: string;
    name: string;
  }>;
}

interface PreviewData {
  count: number;
  users: Array<{
    id: number | string;
    name: string;
    email: string;
    status: string;
    role: string;
    last_login: string;
    created_at: string;
    type: 'user' | 'custom';
  }>;
}

const CreateEmailCampaign: React.FC = () => {
  const [formData, setFormData] = useState<CampaignFormData>({
    title: '',
    subject: '',
    message: '',
    recipient_type: 'criteria',
    criteria: {
      user_status: 'all',
      subscription_status: 'all',
      user_role: 'user',
      inactive_days: '',
      registration_period: 'all',
      include_admins: false
    },
    custom_emails: []
  });

  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');
  const [showBulkInput, setShowBulkInput] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleInputChange = (field: string, value: string | boolean) => {
    if (field.startsWith('criteria.')) {
      const criteriaField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        criteria: {
          ...prev.criteria,
          [criteriaField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear preview when recipient type changes
    if (field === 'recipient_type') {
      setShowPreview(false);
      setPreviewData(null);
    }
  };

  const addCustomEmail = () => {
    if (!newEmail.trim()) {
      setErrors(prev => ({ ...prev, newEmail: 'Email address is required' }));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setErrors(prev => ({ ...prev, newEmail: 'Please enter a valid email address' }));
      return;
    }

    // Check for duplicates
    const isDuplicate = formData.custom_emails.some(item =>
      item.email.toLowerCase() === newEmail.toLowerCase()
    );
    if (isDuplicate) {
      setErrors(prev => ({ ...prev, newEmail: 'This email is already added' }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      custom_emails: [
        ...prev.custom_emails,
        { email: newEmail.trim(), name: newName.trim() }
      ]
    }));

    setNewEmail('');
    setNewName('');
    setErrors(prev => ({ ...prev, newEmail: '' }));
    setSuccessMessage('Email added successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const removeCustomEmail = (index: number) => {
    setFormData(prev => ({
      ...prev,
      custom_emails: prev.custom_emails.filter((_, i) => i !== index)
    }));
  };

  const processBulkEmails = () => {
    if (!bulkEmails.trim()) {
      setErrors(prev => ({ ...prev, bulkEmails: 'Please enter some email addresses' }));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const lines = bulkEmails.split('\n').filter(line => line.trim());
    const newEmails: Array<{ email: string; name: string }> = [];
    const existingEmails = new Set(formData.custom_emails.map(item => item.email.toLowerCase()));
    const invalidEmails: string[] = [];
    const duplicateEmails: string[] = [];

    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      let email = '';
      let name = '';

      if (trimmedLine.includes('<') && trimmedLine.includes('>')) {
        // Format: "John Doe <john@example.com>"
        const match = trimmedLine.match(/^(.+?)\s*<(.+?)>$/);
        if (match) {
          name = match[1].trim();
          email = match[2].trim();
        }
      } else if (trimmedLine.includes(',')) {
        // Format: "John Doe, john@example.com"
        const parts = trimmedLine.split(',').map(p => p.trim());
        if (parts.length >= 2) {
          name = parts[0];
          email = parts[1];
        }
      } else if (emailRegex.test(trimmedLine)) {
        // Format: "john@example.com"
        email = trimmedLine;
      }

      if (!email) {
        invalidEmails.push(trimmedLine);
        return;
      }

      if (!emailRegex.test(email)) {
        invalidEmails.push(email);
        return;
      }

      if (existingEmails.has(email.toLowerCase())) {
        duplicateEmails.push(email);
        return;
      }

      newEmails.push({ email, name });
      existingEmails.add(email.toLowerCase());
    });

    if (invalidEmails.length > 0 || duplicateEmails.length > 0) {
      let errorMessage = '';
      if (invalidEmails.length > 0) {
        errorMessage += `Invalid emails: ${invalidEmails.slice(0, 3).join(', ')}`;
        if (invalidEmails.length > 3) errorMessage += ` and ${invalidEmails.length - 3} more`;
      }
      if (duplicateEmails.length > 0) {
        if (errorMessage) errorMessage += '. ';
        errorMessage += `Duplicate emails: ${duplicateEmails.slice(0, 3).join(', ')}`;
        if (duplicateEmails.length > 3) errorMessage += ` and ${duplicateEmails.length - 3} more`;
      }
      setErrors(prev => ({ ...prev, bulkEmails: errorMessage }));
    }

    if (newEmails.length > 0) {
      setFormData(prev => ({
        ...prev,
        custom_emails: [...prev.custom_emails, ...newEmails]
      }));

      setSuccessMessage(`${newEmails.length} email(s) added successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    }

    setBulkEmails('');
    setShowBulkInput(false);
    setErrors(prev => ({ ...prev, bulkEmails: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Campaign title is required';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Email subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Email message is required';
    }

    if (formData.recipient_type === 'custom' && formData.custom_emails.length === 0) {
      newErrors.custom_emails = 'Please add at least one email address';
    }

    if (formData.recipient_type === 'both' && formData.custom_emails.length === 0) {
      newErrors.custom_emails = 'Please add at least one custom email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const previewRecipients = async () => {
    // Validate before preview
    if (formData.recipient_type === 'custom' && formData.custom_emails.length === 0) {
      setErrors(prev => ({ ...prev, custom_emails: 'Please add at least one email address before previewing' }));
      return;
    }

    setPreviewLoading(true);
    setErrors(prev => ({ ...prev, preview: '' }));

    try {
      console.log('Previewing recipients with data:', {
        recipient_type: formData.recipient_type,
        criteria: formData.criteria,
        custom_emails: formData.custom_emails
      });

      const response: any = await AdminApis.previewEmailRecipients({
        recipient_type: formData.recipient_type,
        criteria: formData.criteria,
        custom_emails: formData.custom_emails
      });

      console.log('Preview response:', response);

      if (response.data && response.data.status === 'success') {
        setPreviewData(response.data.data);
        setShowPreview(true);
      } else {
        const errorMessage = response.data?.message || 'Unknown error occurred';
        console.error('API Error:', errorMessage);
        setErrors(prev => ({ ...prev, preview: 'Error previewing recipients: ' + errorMessage }));
      }
    } catch (error: any) {
      console.error('Error previewing recipients:', error);

      let errorMessage = 'Network error occurred';
      if (error.response) {
        errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
        if (error.response.data?.errors) {
          const validationErrors = Object.values(error.response.data.errors).flat();
          errorMessage = validationErrors.join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setErrors(prev => ({ ...prev, preview: 'Error previewing recipients: ' + errorMessage }));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (sendImmediately: boolean = false) => {
    if (!validateForm()) return;

    setLoading(true);
    setErrors(prev => ({ ...prev, submit: '' }));

    try {
      console.log('Submitting campaign with data:', {
        ...formData,
        send_immediately: sendImmediately
      });

      const response: any = await AdminApis.createEmailCampaign({
        ...formData,
        send_immediately: sendImmediately
      });

      console.log('Submit response:', response);

      if (response.data && response.data.status === 'success') {
        setSuccessMessage(sendImmediately ? 'Campaign created and sent successfully!' : 'Campaign created successfully!');
        setTimeout(() => {
          window.location.href = '/admin/email-campaigns';
        }, 2000);
      } else {
        const errorMessage = response.data?.message || 'Unknown error occurred';
        console.error('API Error:', errorMessage);
        setErrors(prev => ({ ...prev, submit: 'Error creating campaign: ' + errorMessage }));
      }
    } catch (error: any) {
      console.error('Error creating campaign:', error);

      let errorMessage = 'Network error occurred';
      if (error.response) {
        errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
        if (error.response.data?.errors) {
          const validationErrors = Object.values(error.response.data.errors).flat();
          errorMessage = validationErrors.join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setErrors(prev => ({ ...prev, submit: 'Error creating campaign: ' + errorMessage }));
    } finally {
      setLoading(false);
    }
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('message') as HTMLTextAreaElement;
    if (textarea) {
      const cursorPosition = textarea.selectionStart;
      const textBefore = formData.message.substring(0, cursorPosition);
      const textAfter = formData.message.substring(cursorPosition);
      const newMessage = textBefore + variable + textAfter;

      setFormData(prev => ({
        ...prev,
        message: newMessage
      }));

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(cursorPosition + variable.length, cursorPosition + variable.length);
      }, 0);
    }
  };

  const getTotalRecipientsCount = () => {
    if (!previewData) {
      // Fallback count based on form data
      if (formData.recipient_type === 'custom') return formData.custom_emails.length;
      if (formData.recipient_type === 'criteria') return 0; // Unknown until preview
      return formData.custom_emails.length; // For 'both', at least show custom count
    }

    const criteriaCount = previewData.users.filter(u => u.type === 'user').length || 0;
    const customCount = previewData.users.filter(u => u.type === 'custom').length || 0;

    if (formData.recipient_type === 'criteria') return criteriaCount;
    if (formData.recipient_type === 'custom') return customCount;
    return criteriaCount + customCount;
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <span className="text-green-800">{successMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => window.location.href = '/admin/email-campaigns'}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Email Campaign</h1>
          <p className="text-gray-600">Create and send targeted email campaigns</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Campaign Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Campaign Details</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter campaign title"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Subject *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.subject ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter email subject"
                />
                {errors.subject && (
                  <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Message *
                </label>
                <textarea
                  id="message"
                  rows={8}
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter your email message here..."
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                )}

                {/* Variable Buttons */}
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => insertVariable('{{first_name}}')}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    First Name
                  </button>
                  <button
                    type="button"
                    onClick={() => insertVariable('{{last_name}}')}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    Last Name
                  </button>
                  <button
                    type="button"
                    onClick={() => insertVariable('{{full_name}}')}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    Full Name
                  </button>
                  <button
                    type="button"
                    onClick={() => insertVariable('{{email}}')}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    Email
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Click the buttons above to insert personalization variables into your message
                </p>
              </div>
            </div>
          </div>

          {/* Recipient Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Recipients</h2>

            {/* Recipient Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Who would you like to send this campaign to?
              </label>
              <div className="space-y-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="recipient_type"
                    value="criteria"
                    checked={formData.recipient_type === 'criteria'}
                    onChange={(e) => handleInputChange('recipient_type', e.target.value)}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <Users className="inline h-4 w-4 mr-1" />
                    Existing users (based on criteria)
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="recipient_type"
                    value="custom"
                    checked={formData.recipient_type === 'custom'}
                    onChange={(e) => handleInputChange('recipient_type', e.target.value)}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <Mail className="inline h-4 w-4 mr-1" />
                    Custom email addresses
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="recipient_type"
                    value="both"
                    checked={formData.recipient_type === 'both'}
                    onChange={(e) => handleInputChange('recipient_type', e.target.value)}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <Target className="inline h-4 w-4 mr-1" />
                    Both existing users and custom emails
                  </span>
                </label>
              </div>
            </div>

            {/* User Criteria Section */}
            {(formData.recipient_type === 'criteria' || formData.recipient_type === 'both') && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="text-md font-medium text-gray-900 mb-4">User Criteria</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User Status</label>
                    <select
                      value={formData.criteria.user_status}
                      onChange={(e) => handleInputChange('criteria.user_status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="all">All Users</option>
                      <option value="active">Active Users Only</option>
                      <option value="inactive">Inactive Users</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Status</label>
                    <select
                      value={formData.criteria.subscription_status}
                      onChange={(e) => handleInputChange('criteria.subscription_status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="all">All Users</option>
                      <option value="subscribed">Subscribed Users</option>
                      <option value="unsubscribed">Unsubscribed Users</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User Role</label>
                    <select
                      value={formData.criteria.user_role}
                      onChange={(e) => handleInputChange('criteria.user_role', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="all">All Roles</option>
                      <option value="user">Regular Users</option>
                      <option value="admin">Admins</option>
                      <option value="superadmin">Super Admins</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Inactive For (Days)</label>
                    <input
                      type="number"
                      value={formData.criteria.inactive_days}
                      onChange={(e) => handleInputChange('criteria.inactive_days', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="e.g., 30"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Period</label>
                    <select
                      value={formData.criteria.registration_period}
                      onChange={(e) => handleInputChange('criteria.registration_period', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="last_month">Last Month</option>
                    </select>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="include_admins"
                      checked={formData.criteria.include_admins}
                      onChange={(e) => handleInputChange('criteria.include_admins', e.target.checked)}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="include_admins" className="ml-2 block text-sm text-gray-700">
                      Include Admins & Super Admins
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Custom Emails Section */}
            {(formData.recipient_type === 'custom' || formData.recipient_type === 'both') && (
              <div className="mb-6 p-4 bg-green-50 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-md font-medium text-gray-900">Custom Email Addresses</h3>
                  <button
                    type="button"
                    onClick={() => setShowBulkInput(!showBulkInput)}
                    className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {showBulkInput ? 'Single Entry' : 'Bulk Add'}
                  </button>
                </div>

                {!showBulkInput ? (
                  // Single email entry
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.newEmail ? 'border-red-500' : 'border-gray-300'
                            }`}
                          placeholder="Enter email address"
                          onKeyPress={(e) => e.key === 'Enter' && addCustomEmail()}
                        />
                        {errors.newEmail && (
                          <p className="mt-1 text-sm text-red-600">{errors.newEmail}</p>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors"
                          placeholder="Name (optional)"
                          onKeyPress={(e) => e.key === 'Enter' && addCustomEmail()}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={addCustomEmail}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </button>
                    </div>
                  </div>
                ) : (
                  // Bulk email entry
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bulk Add Emails
                      </label>
                      <textarea
                        value={bulkEmails}
                        onChange={(e) => setBulkEmails(e.target.value)}
                        rows={6}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.bulkEmails ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder={`Enter emails in any of these formats (one per line):
john@example.com
Jane Doe <jane@example.com>
Bob Smith, bob@example.com`}
                      />
                      {errors.bulkEmails && (
                        <p className="mt-1 text-sm text-red-600">{errors.bulkEmails}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Supported formats: email@domain.com, "Name &lt;email@domain.com&gt;", or "Name, email@domain.com"
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={processBulkEmails}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      Process Emails
                    </button>
                  </div>
                )}

                {/* Custom Email List */}
                {formData.custom_emails.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Added Email Addresses ({formData.custom_emails.length})
                    </h4>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {formData.custom_emails.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                          <div>
                            <div className="font-medium text-gray-900">{item.email}</div>
                            {item.name && <div className="text-sm text-gray-500">{item.name}</div>}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeCustomEmail(index)}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {errors.custom_emails && (
                  <p className="mt-2 text-sm text-red-600">{errors.custom_emails}</p>
                )}
              </div>
            )}

            {/* Preview Recipients Button */}
            <div className="mt-6">
              <button
                type="button"
                onClick={previewRecipients}
                disabled={previewLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {previewLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4" />
                    Preview Recipients
                  </>
                )}
              </button>
              {errors.preview && (
                <p className="mt-2 text-sm text-red-600">{errors.preview}</p>
              )}
            </div>
          </div>

          {/* Error Display for Form Submission */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-800">{errors.submit}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save as Draft
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleSubmit(true)}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Create & Send Now
                </>
              )}
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Help Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">Email Variables</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Use variables to personalize your emails:
                </p>
                <ul className="text-sm text-blue-700 mt-2 space-y-1">
                  <li>• <code>{'{{first_name}}'}</code> - User's first name</li>
                  <li>• <code>{'{{last_name}}'}</code> - User's last name</li>
                  <li>• <code>{'{{full_name}}'}</code> - User's full name</li>
                  <li>• <code>{'{{email}}'}</code> - User's email address</li>
                </ul>
                <p className="text-xs text-blue-600 mt-2">
                  For custom emails without names, first_name will use the email username part.
                </p>
              </div>
            </div>
          </div>

          {/* Recipient Preview */}
          {showPreview && previewData && (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-2 mb-4">
                <Target className="h-5 w-5 text-green-500" />
                <h3 className="font-medium text-gray-900">Recipients Preview</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-900">Total Recipients</span>
                  </div>
                  <span className="text-xl font-bold text-green-900">{getTotalRecipientsCount()}</span>
                </div>

                {/* Breakdown by type */}
                {formData.recipient_type === 'both' && previewData && (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-blue-50 rounded text-center">
                      <div className="text-sm font-medium text-blue-900">Users</div>
                      <div className="text-lg font-bold text-blue-900">
                        {previewData.users.filter(u => u.type === 'user').length}
                      </div>
                    </div>
                    <div className="p-2 bg-green-50 rounded text-center">
                      <div className="text-sm font-medium text-green-900">Custom</div>
                      <div className="text-lg font-bold text-green-900">
                        {previewData.users.filter(u => u.type === 'custom').length}
                      </div>
                    </div>
                  </div>
                )}

                {previewData.users.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Sample Recipients:</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {previewData.users.map((user, index) => (
                        <div
                          key={`${user.type}-${user.id || index}`}
                          className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm"
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${user.type === 'user' ? 'bg-blue-500' : 'bg-green-500'
                            }`}>
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{user.name}</div>
                            <div className="text-gray-500 truncate">{user.email}</div>
                          </div>
                          <div className="flex flex-col items-end text-xs">
                            <span className={`px-2 py-1 rounded-full text-xs ${user.type === 'user'
                                ? user.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                              }`}>
                              {user.type === 'user' ? user.status : 'custom'}
                            </span>
                            {user.type === 'user' && (
                              <span className="text-gray-400 mt-1">{user.role}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {getTotalRecipientsCount() > 10 && (
                      <p className="text-sm text-gray-500 mt-2">
                        Showing first 10 of {getTotalRecipientsCount()} recipients
                      </p>
                    )}
                  </div>
                )}

                {getTotalRecipientsCount() === 0 && (
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm text-yellow-800">
                      No recipients found. Please adjust your criteria or add custom emails.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Custom Emails Summary */}
          {formData.custom_emails.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-2 mb-3">
                <UserPlus className="h-5 w-5 text-green-500" />
                <h3 className="font-medium text-gray-900">Custom Emails</h3>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-900">{formData.custom_emails.length}</div>
                <div className="text-sm text-green-700">Custom recipients added</div>
              </div>
              <div className="mt-3 text-xs text-gray-500">
                <p>• {formData.custom_emails.filter(e => e.name).length} with names</p>
                <p>• {formData.custom_emails.filter(e => !e.name).length} email only</p>
              </div>
            </div>
          )}

          {/* Tips Card */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-900">Tips for Success</h3>
                <ul className="text-sm text-yellow-800 mt-2 space-y-1">
                  <li>• Keep subject lines under 50 characters</li>
                  <li>• Personalize with user variables</li>
                  <li>• Preview recipients before sending</li>
                  <li>• Test with a small group first</li>
                  <li>• Include clear call-to-action</li>
                  <li>• For custom emails, provide names for better personalization</li>
                  <li>• Check spam folder if emails don't arrive</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Campaign Summary */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">Campaign Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Target Type:</span>
                <span className="font-medium">
                  {formData.recipient_type === 'criteria' && 'Users Only'}
                  {formData.recipient_type === 'custom' && 'Custom Emails Only'}
                  {formData.recipient_type === 'both' && 'Users + Custom Emails'}
                </span>
              </div>
              {formData.custom_emails.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Custom Emails:</span>
                  <span className="font-medium">{formData.custom_emails.length}</span>
                </div>
              )}
              {showPreview && previewData && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Recipients:</span>
                  <span className="font-medium text-green-600">{getTotalRecipientsCount()}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Variables Used:</span>
                <span className="font-medium">
                  {[
                    formData.message.includes('{{first_name}}') && 'First Name',
                    formData.message.includes('{{last_name}}') && 'Last Name',
                    formData.message.includes('{{full_name}}') && 'Full Name',
                    formData.message.includes('{{email}}') && 'Email'
                  ].filter(Boolean).join(', ') || 'None'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEmailCampaign;